import { createSlice } from '@reduxjs/toolkit';

import { DictTypes } from '@/constants';
import { positionsApi } from '@/integrations/adminApi/positionsApi';
import { rolesActionsApi } from '@/integrations/adminApi/rolesActionsApi';
import { userApi } from '@/integrations/adminApi/usersApi';
import type { TPosition } from '@/types/position.type';
import type { TRoleAction } from '@/types/roleAction.type';
import type { TUserDto, TUserResponse } from '@/types/user.type';

import { createAsyncThunk } from '../create-async-thunk';
import { getOptimizedDictionaries } from '../dict.slice';

type TUserSliceProps = {
  users: TUserDto[];
  positions: TPosition[];
  roles_actions: TRoleAction[];
  total: number | undefined;
  isLoading: boolean;
  user: TUserResponse | null;
  position: TPosition | null;
  role_action: TRoleAction | null;
};

const initialState: TUserSliceProps = {
  users: [],
  positions: [],
  roles_actions: [],
  total: 0,
  isLoading: false,
  user: null,
  position: null,
  role_action: null
};

export const getAllPositions = createAsyncThunk(
  'admin/positions/getAllPositions',
  async (data: {
    limit?: number;
    page?: number;
    searchTerm?: string;
    isActive?: boolean;
  }) => {
    const res = await positionsApi.getAdminAllPositions(
      data.limit,
      data.page,
      data.searchTerm,
      data.isActive
    );
    return {
      total: res.total,
      positions: res.data
    };
  }
);

export const createPosition = createAsyncThunk(
  'admin/user/createPosition',
  async (data: TPosition, { dispatch }) => {
    const response = await positionsApi.createPosition(data);
    await dispatch(
      getOptimizedDictionaries({
        dictTypes: [DictTypes.Positions],
        updatingDicts: [DictTypes.Positions]
      })
    );
    return response;
  }
);
export const updatePosition = createAsyncThunk(
  'admin/user/updatePosition',
  async (data: TPosition, { dispatch }) => {
    const response = await positionsApi.updatePosition(data);
    await dispatch(
      getOptimizedDictionaries({
        dictTypes: [DictTypes.Positions],
        updatingDicts: [DictTypes.Positions]
      })
    );
    return response;
  }
);

export const getPositionById = createAsyncThunk(
  'admin/getPositionById',
  async (userId: string) => {
    return await positionsApi.getPositionById(userId);
  }
);

export const getRolesActions = createAsyncThunk(
  'admin/role_actions/getRolesActions',
  async (data: { limit?: number; page?: number; searchTerm?: string }) => {
    const res = await rolesActionsApi.getAllRolesActions(
      data.limit,
      data.page,
      data.searchTerm
    );
    return {
      total: res.total,
      roles_actions: res.data
    };
  }
);

export const createRolesAction = createAsyncThunk(
  '/admin/users/creteRolesActions',
  async (data: TRoleAction) => {
    return rolesActionsApi.createRolesAction(data);
  }
);

export const updateRolesAction = createAsyncThunk(
  'admin/users/updateRolesAction',
  async (data: TRoleAction) => {
    return rolesActionsApi.updateRolesAction(data);
  }
);

export const getRolesById = createAsyncThunk(
  'admin/getRolesById',
  async (userId: string) => {
    return await rolesActionsApi.getRolesById(userId);
  }
);

export const getUsersById = createAsyncThunk(
  'admin/getUserById',
  async (userId: string) => {
    return await userApi.getAdminUserById(userId);
  }
);

export const getAdminAllUsers = createAsyncThunk(
  'admin/getAdminAllUsers',
  async (data: {
    limit?: number;
    offset?: number;
    searchTerm?: string;
    isActive?: boolean;
    position?: string;
  }) => {
    const res = await userApi.getAdminAllUsers(
      data.limit,
      data.offset,
      data.searchTerm,
      data.isActive,
      data.position
    );
    return {
      total: res.total,
      users: res.data
    };
  }
);

export const adminUsersSlice = createSlice({
  name: 'admin/users',
  initialState,
  reducers: {
    resetUserState(state) {
      state.user = null;
    },
    resetRoleActionState(state) {
      state.role_action = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAdminAllUsers.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getAdminAllUsers.fulfilled, (state, action) => {
        state.users = action.payload.users;
        state.total = action.payload.total;
        state.isLoading = false;
      })
      .addCase(getAdminAllUsers.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(getRolesActions.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getRolesActions.fulfilled, (state, action) => {
        state.roles_actions = action.payload.roles_actions;
        state.isLoading = false;
        state.total = action.payload.total;
      })
      .addCase(getRolesActions.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(getAllPositions.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getAllPositions.fulfilled, (state, action) => {
        state.positions = action.payload.positions;
        state.isLoading = false;
        state.total = action.payload.total;
      })
      .addCase(getAllPositions.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(getUsersById.fulfilled, (state, action) => {
        state.user = action.payload;
      })
      .addCase(getUsersById.pending, (state) => {
        state.user = null;
      })
      .addCase(getPositionById.fulfilled, (state, action) => {
        state.position = action.payload;
      })
      .addCase(getPositionById.pending, (state) => {
        state.position = null;
      })
      .addCase(getRolesById.fulfilled, (state, action) => {
        state.role_action = action.payload;
      })
      .addCase(getRolesById.pending, (state) => {
        state.role_action = null;
      });
  }
});

export const { resetUserState, resetRoleActionState } = adminUsersSlice.actions;
